type RankLabelLinesArgs = {
  label: string | null;
  /** Already formatted and parenthesised, e.g. `(4.7M)`. */
  valueLabel: string;
  /** What the shape can draw with the value reserved first, from `toLeafText`. */
  withValue: string[];
  /** What it can draw of the name alone, where the value is given up. */
  nameOnly: string[];
};

const collapseSpace = (text: string) => text.split(/\s+/).join(' ').trim();

/**
 * The lines a shape draws, ranked by how much of the name survives.
 *
 * `toLeafText` reserves the value first, which is right for a shape with room for both and wrong
 * for one without: a tile with room for `Payroll` drew `Pa… (1)`, and a ring too thin for
 * `(15,4 mio. $)` drew nothing at all. A name is what identifies the shape and its area already
 * carries the value, so:
 *
 * 1. the name whole, with the value
 * 2. the name whole, alone
 * 3. whichever of the two shows more of the name
 * 4. the value alone, which at least says how big it is
 */
export const rankLabelLines = ({
  label,
  valueLabel,
  withValue,
  nameOnly,
}: RankLabelLinesArgs): string[] => {
  const wholeName = label ? collapseSpace(label) : '';
  const nameShown = (lines: string[]) =>
    collapseSpace(lines.join(' ').replace(valueLabel, ''));
  const keepsName = (lines: string[]) =>
    lines.length > 0 && nameShown(lines) === wholeName;

  if (keepsName(withValue)) {
    return withValue;
  }
  if (keepsName(nameOnly)) {
    return nameOnly;
  }
  const isBareValue = withValue.join(' ') === valueLabel;
  if (!withValue.length || isBareValue) {
    return nameOnly.length ? nameOnly : withValue;
  }
  if (!nameOnly.length) {
    return withValue;
  }
  return nameShown(nameOnly).length > nameShown(withValue).length
    ? nameOnly
    : withValue;
};
